import { useTranslation } from 'react-i18next'
import { LangText } from '@/components'
import { Form, Input, DatePicker, TimePicker, Select, InputNumber, Row, Col, FormInstance } from 'antd'
import { preFixImgUrl } from '@/utils'
import { BookCarTypeEnum } from '@/service/api/types'
import dayjs from 'dayjs'

const { TextArea } = Input
const FormItem = Form.Item

// 租车申请表
const CarRentalForm = ({ children, form }: { children: React.ReactNode, form: FormInstance<any> }) => {
  const { t } = useTranslation();
  const pickupDate = Form.useWatch('pickupDate', form)
  const returnDate = Form.useWatch('returnDate', form)
  // 车型选项
  const vehicleTypes = [
    { label: t('010dadcf'), value: BookCarTypeEnum.economy_car },
    { label: t('e0d04b58'), value: BookCarTypeEnum.business_car },
    { label: t('a1abdb2d'), value: BookCarTypeEnum.luxury_suv },
    { label: t('f5d08804'), value: BookCarTypeEnum.SEVEN_SEATER_MPV },
  ]

  return (
    <div className='gap-[50px] flex flex-col'>
      {/* 1. 个人信息 */}
      <div className='bg-white rounded-[20px] px-[30px] pt-[33px] pb-[40px]'>
        <div className='flex items-center mb-6'>
          <div className='bg-[#59C55F] rounded-[50%] w-[32px] h-[32px] flex items-center justify-center shrink-0'>
            <span className='text-white text-[20px] font-bold'>1</span>
          </div>
          <LangText lngKey='b2e99632' className='text-[30px] text-black font-bold ml-3'>{t('b2e99632')}</LangText>
        </div>
        <div className='flex flex-col gap-4'>
          <Row gutter={50}>
            <Col span={12}>
              <FormItem
                name='name'
                label={<LangText lngKey='d4d03b81' className='text-[16px]'>{t('d4d03b81')}</LangText>}
                rules={[{ required: true, message: t('c4878501') }]}
              >
                <Input maxLength={100} placeholder={t('c4878501')} className='h-[44px]' />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                name='phone'
                label={<LangText lngKey='1e5b1241' className='text-[16px]'>{t('1e5b1241')}</LangText>}
                rules={[
                  { required: true, message: t('6752981a') },
                  { pattern: /^\d{8, 13}$/, message: t('7a597350') }
                ]}
              >
                <Input maxLength={20} placeholder={t('6752981a')} className='h-[44px]' />
              </FormItem>
            </Col>
          </Row>
          <FormItem
            name='email'
            label={<LangText lngKey='3b3ca16d' className='text-[16px]'>{t('3b3ca16d')}</LangText>}
            rules={[
              { type: 'email', message: t('f962863d') }
            ]}
          >
            <Input maxLength={100} placeholder={t('cdf8c59f')} className='h-[44px]' />
          </FormItem>
        </div>
      </div>

      {/* 2. 取车信息 */}
      <div className='bg-white rounded-[20px] px-[30px] pt-[33px] pb-[40px]'>
        <div className='flex items-center mb-6'>
          <div className='bg-[#59C55F] rounded-[50%] w-[32px] h-[32px] flex items-center justify-center shrink-0'>
            <span className='text-white text-[20px] font-bold'>2</span>
          </div>
          <LangText lngKey='25d318bc' className='text-[30px] text-black font-bold ml-3'>{t('25d318bc')}</LangText>
        </div>
        <div className='flex flex-col gap-4'>
          <Row gutter={50}>
            <Col span={12}>
              <FormItem
                name='pickupDate'
                label={<LangText lngKey='38eb496e' className='text-[16px]'>{t('38eb496e')}</LangText>}
                rules={[{ required: true, message: t('d2ce7850') }]}
              >
                <DatePicker
                  suffixIcon={<img src={preFixImgUrl('88714c463222c4ffec7356ab91c7460f.png')} className='h-[22px]'/>}
                  placeholder={t('31b1a0cb')}
                  className='w-full'
                  style={{ height: '44px' }}
                  minDate={dayjs().add(1, 'day')}
                  format='YYYY/MM/DD'
                  disabledDate={(current) => {
                    return returnDate ? current > returnDate?.add(-1, 'day') : false
                  }}
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                name='pickupTime'
                label={<LangText lngKey='dbfce466' className='text-[16px]'>{t('dbfce466')}</LangText>}
                rules={[{ required: true, message: t('f96c887b') }]}
              >
                <TimePicker
                  suffixIcon={<img src={preFixImgUrl('5d352e763081f0de96874ffe490dcda1.png')} className='h-[22px]'/>}
                  placeholder='--:--'
                  className='w-full'
                  style={{ height: '44px' }}
                  format='HH:mm'
                />
              </FormItem>
            </Col>
          </Row>


          <FormItem
            name='pickupLocation'
            label={<LangText lngKey='892767fe' className='text-[16px]'>{t('892767fe')}</LangText>}
            rules={[{ required: true, message: t('26466ebc') }]}
          >
            <Input
              placeholder={t('77e19c8a')}
              className='h-[44px]'
              maxLength={100}
            />
          </FormItem>
        </div>
      </div>

      {/* 3. 还车信息 */}
      <div className='bg-white rounded-[20px] px-[30px] pt-[33px] pb-[40px]'>
        <div className='flex items-center mb-6'>
          <div className='bg-[#59C55F] rounded-[50%] w-[32px] h-[32px] flex items-center justify-center shrink-0'>
            <span className='text-white text-[20px] font-bold'>3</span>
          </div>
          <LangText lngKey='8e34305a' className='text-[30px] text-black font-bold ml-3'>{t('8e34305a')}</LangText>
        </div>
        <div className='flex flex-col gap-4'>
          <Row gutter={50}>
            <Col span={12}>
              <FormItem
                name='returnDate'
                label={<LangText lngKey='bc1c912f' className='text-[16px]'>{t('bc1c912f')}</LangText>}
                rules={[{ required: true, message: t('119a8b04') }]}
              >
                <DatePicker
                  suffixIcon={<img src={preFixImgUrl('88714c463222c4ffec7356ab91c7460f.png')} className='h-[22px]'/>}
                  placeholder={t('31b1a0cb')}
                  className='w-full'
                  minDate={dayjs().add(1, 'day')}
                  style={{ height: '44px' }}
                  format='YYYY/MM/DD'
                  disabledDate={(current) => {
                    return pickupDate ? current < pickupDate?.add(1, 'day') : false
                  }}
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                name='returnTime'
                label={<LangText lngKey='7371674a' className='text-[16px]'>{t('7371674a')}</LangText>}
                rules={[{ required: true, message: t('2f5a38d7') }]}
              >
                <TimePicker
                  suffixIcon={<img src={preFixImgUrl('5d352e763081f0de96874ffe490dcda1.png')} className='h-[22px]'/>}
                  placeholder='--:--'
                  className='w-full'
                  style={{ height: '44px' }}
                  format='HH:mm'
                />
              </FormItem>
            </Col>
          </Row>


          <FormItem
            name='returnLocation'
            label={<LangText lngKey='32174194' className='text-[16px]'>{t('32174194')}</LangText>}
            rules={[{ required: true, message: t('4ac889f3') }]}
          >
            <Input
              placeholder={t('77e19c8a')}
              className='h-[44px]'
              maxLength={100}
            />
          </FormItem>
        </div>
      </div>

      {/* 4. 车辆需求 */}
      <div className='bg-white rounded-[20px] px-[30px] pt-[33px] pb-[40px]'>
        <div className='flex items-center mb-6'>
          <div className='bg-[#59C55F] rounded-[50%] w-[32px] h-[32px] flex items-center justify-center shrink-0'>
            <span className='text-white text-[20px] font-bold'>4</span>
          </div>
          <LangText lngKey='90a32919' className='text-[30px] text-black font-bold ml-3'>{t('90a32919')}</LangText>
        </div>
        <div className='flex flex-col gap-4'>
          <Row gutter={50}>
            <Col span={12}>
              <FormItem
                name='carType'
                label={<LangText lngKey='51607499' className='text-[16px]'>{t('51607499')}</LangText>}
                rules={[{ required: true, message: t('31e5b07d') }]}
              >
                <Select
                  placeholder={t('31e5b07d')}
                  className='w-full'
                  style={{ height: '44px' }}
                  options={vehicleTypes}
                />
              </FormItem>
            </Col>
            <Col span={12}>
              <FormItem
                name='passengerCount'
                label={<LangText lngKey='257cab60' className='text-[16px]'>{t('257cab60')}</LangText>}
                rules={[{ required: true, message: t('f9ea859d') }]}
              >
                <InputNumber
                  placeholder={t('b6808a73')}
                  className='w-full'
                  style={{ height: '44px', width: '100%' }}
                  min={1}
                  max={10}
                />
              </FormItem>
            </Col>
          </Row>
          <Row gutter={50}>
            <Col span={12}>
              <FormItem
                name='luggageCount'
                label={<LangText lngKey='52aff3ed' className='text-[16px]'>{t('52aff3ed')}</LangText>}
              >
                <InputNumber
                  placeholder={t('7bfb22c4')}
                  className='w-full'
                  style={{ height: '44px', width: '100%' }}
                  min={0}
                />
              </FormItem>
            </Col>
          </Row>

          <FormItem
            name='specialRequirement'
            label={<LangText lngKey='e7402076' className='text-[16px]'>{t('e7402076')}</LangText>}
          >
            <TextArea
              placeholder={t('e4b04250')}
              rows={4}
              className='resize-none'
              maxLength={1000}
            />
          </FormItem>
        </div>
      </div>
      {children}
      <div className='bg-white rounded-[20px] flex flex-col px-[30px] pt-[32px] pb-[40px] items-center mb-[112px]'>
        <LangText lngKey='a62f1b9f' className='text-[30px] text-black font-bold mb-6'>{t('a62f1b9f')}</LangText>
        <div className='bg-[#F3FAF1] rounded-[10px] flex  w-[1000px] py-[30px] justify-around'>
          <div className='flex items-center gap-[10px]'>
            <img src={preFixImgUrl('35d4cb318e7a599c05beef7f23ad00f7.png')}/>
            <LangText lngKey='7956a9a7' className='text-[20px] text-black'>{t('7956a9a7')}</LangText>
          </div>
          <div className='flex items-center gap-[10px]'>
            <img src={preFixImgUrl('35d4cb318e7a599c05beef7f23ad00f7.png')}/>
            <LangText lngKey='9e06568f' className='text-[20px] text-black'>{t('9e06568f')}</LangText>
          </div>
          <div className='flex items-center gap-[10px]'>
            <img src={preFixImgUrl('35d4cb318e7a599c05beef7f23ad00f7.png')}/>
            <LangText lngKey='616fdcd3' className='text-[20px] text-black'>{t('616fdcd3')}</LangText>
          </div>
          <div className='flex items-center gap-[10px]'>
            <img src={preFixImgUrl('35d4cb318e7a599c05beef7f23ad00f7.png')}/>
            <LangText lngKey='4f0e1879' className='text-[20px] text-black'>{t('4f0e1879')}</LangText>
          </div>

        </div>
      </div>
    </div>
  )
}

export default CarRentalForm  